import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AtSignIcon, CheckIcon, ChevronDownIcon, MessageSquareIcon, SearchIcon, SendIcon, XIcon } from 'lucide-react';
import { CaseType, Ticket, Status } from '../data/mockData';
interface TicketDetailModalProps {
  ticket: Ticket;
  assignees: string[];
  onClose: () => void;
  onUpdateTicket: (updatedTicket: Ticket) => void;
}
interface Comment {
  id: string;
  author: string;
  text: string;
  timestamp: string;
}
export function TicketDetailModal({
  ticket,
  assignees,
  onClose,
  onUpdateTicket
}: TicketDetailModalProps) {
  const [status, setStatus] = useState<Status>(ticket.status);
  const [caseType, setCaseType] = useState<CaseType | ''>(ticket.caseType ?? '');
  const [isAssigneeMenuOpen, setIsAssigneeMenuOpen] = useState(false);
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [commentText, setCommentText] = useState('');
  const assigneeMenuRef = useRef<HTMLDivElement>(null);
  const assigneeSearchRef = useRef<HTMLInputElement>(null);
  const matchingAssignees = useMemo(() => assignees.filter(assignee => assignee.toLocaleLowerCase().includes(assigneeSearch.trim().toLocaleLowerCase())), [assigneeSearch, assignees]);
  const [comments, setComments] = useState<Comment[]>([{
    id: 'c1',
    author: ticket.submittedBy,
    text: 'Please look into this as soon as possible. It is impacting my workflow.',
    timestamp: new Date(ticket.createdDate).toLocaleString()
  }]);
  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStatus = e.target.value as Status;
    setStatus(newStatus);
    onUpdateTicket({
      ...ticket,
      status: newStatus
    });
  };
  const handleCaseTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newCaseType = e.target.value as CaseType | '';
    setCaseType(newCaseType);
    onUpdateTicket({
      ...ticket,
      caseType: newCaseType || undefined
    });
  };
  useEffect(() => {
    if (!isAssigneeMenuOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (!assigneeMenuRef.current?.contains(event.target as Node)) {
        setIsAssigneeMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    assigneeSearchRef.current?.focus();
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isAssigneeMenuOpen]);
  const handleAssigneeChange = (assignee: string) => {
    onUpdateTicket({
      ...ticket,
      assignedTo: assignee
    });
    setIsAssigneeMenuOpen(false);
    setAssigneeSearch('');
  };
  const handleTagSubmitter = () => {
    setCommentText(prev => `${prev} @${ticket.submittedBy} `);
  };
  const handlePostComment = () => {
    if (!commentText.trim()) return;
    const newComment: Comment = {
      id: Date.now().toString(),
      author: 'Admin User',
      text: commentText,
      timestamp: new Date().toLocaleString()
    };
    setComments([...comments, newComment]);
    setCommentText('');
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" data-id="element-71" data-mp="3f3hqi0uac5qk">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden" data-id="element-72" data-mp="3jzqbhuv5yt5n">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50" data-id="element-73" data-mp="3a33rt9ekq857">
          <div data-id="element-74" data-mp="2wbyn3swcmpo0">
            <div className="flex items-center space-x-3" data-id="element-75" data-mp="2wbglshutxl98">
              <span className="text-sm font-medium text-blue-600 bg-blue-50 px-2.5 py-0.5 rounded-md border border-blue-100" data-id="element-76" data-mp="312owdh7wbcnz">
                {ticket.id}
              </span>
              <h2 className="text-lg font-bold text-gray-900" data-id="element-77" data-mp="2mc7trujiygpi">
                {ticket.subject}
              </h2>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-78" data-mp="2dd4d0x7tyde6">
            <XIcon className="w-5 h-5" data-id="element-79" data-mp="2abhya97eltqs" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6" data-id="element-80" data-mp="25coi290qfomx">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-81" data-mp="25d6ofyh3zj8k">
            {/* Left Column: Details */}
            <div className="md:col-span-1 space-y-6" data-id="element-82" data-mp="20kdg70mflsrq">
              <div data-id="element-83" data-mp="1vnet3216qm31">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3" data-id="element-84" data-mp="1vltyxdis955p">
                  Ticket Details
                </h3>
                <div className="space-y-4 text-sm" data-id="element-85" data-mp="1rixav7ah4gud">
                  <div data-id="element-86" data-mp="1mlduo1u7ttzs">
                    <span className="block text-gray-500 mb-1" data-id="element-87" data-mp="1mkvq8hxeg9gs">
                      Submitted By
                    </span>
                    <div className="font-medium text-gray-900" data-id="element-88" data-mp="1co0u25olml5o">
                      {ticket.submittedBy}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-89" data-mp="3c1d7bsx1m1k6">
                      {ticket.role} • {ticket.department}
                    </div>
                  </div>
                  <div data-id="element-90" data-mp="3fj0yrme5qvs6">
                    <span className="block text-gray-500 mb-1" data-id="element-91" data-mp="3fjuaz2vvydhp">Priority</span>
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium border ${ticket.priority === 'Critical' ? 'bg-red-100 text-red-700 border-red-200' : ticket.priority === 'High' ? 'bg-orange-100 text-orange-700 border-orange-200' : ticket.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' : 'bg-green-100 text-green-700 border-green-200'}`} data-id="element-92" data-mp="3jke6y34q3b29">
                      {ticket.priority}
                    </span>
                  </div>
                  <div data-id="element-93" data-mp="35jtmz3n2n976">
                    <span className="block text-gray-500 mb-1" data-id="element-94" data-mp="35ja67q95qquq">Status</span>
                    <select value={status} onChange={handleStatusChange} className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-medium text-gray-900" data-id="element-95" data-mp="3akzg32kn8yh8">
                      <option value="New" data-id="element-96" data-mp="4a6qg1k53oq">New</option>
                      <option value="Working" data-id="element-97" data-mp="3rc4qe9fp4rl0">Working</option>
                      <option value="Waiting for Response" data-id="element-98" data-mp="3rbmooxa9ll5p">Waiting for Response</option>
                      <option value="On Hold" data-id="element-99" data-mp="a393p1cynavw">On Hold</option>
                      <option value="Escalated" data-id="element-100" data-mp="544re1uqox1c">Escalated</option>
                      <option value="Closed" data-id="element-101" data-mp="52tsafc8odgk">Closed</option>
                    </select>
                  </div>
                  <div data-id="element-102" data-mp="38leqmtlf9kwd">
                    <label htmlFor="ticket-case-type" className="block text-gray-500 mb-1" data-id="element-103" data-mp="38m68l1m3jk46">
                      Case Type
                    </label>
                    <select id="ticket-case-type" value={caseType} onChange={handleCaseTypeChange} className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-medium text-gray-900" data-id="element-104" data-mp="3ncdu5q7ehj93">
                      <option value="" data-id="element-105" data-mp="15btemean5805">Standard workflow case</option>
                      <option value="DNC" data-id="element-106" data-mp="1a432zpw3y00b">DNC</option>
                      <option value="Create Contact" data-id="element-107" data-mp="1a4w3480k9r9h">Create Contact</option>
                      <option value="Laptop Request" data-id="element-108" data-mp="vfzv2ayi8e8v">Laptop Request</option>
                      <option value="Business Cards" data-id="element-109" data-mp="10d8cg9btwe92">Business Cards</option>
                    </select>
                  </div>
                  <div ref={assigneeMenuRef} className="relative" data-id="element-110" data-mp="1o4nleky41tta">
                    <label htmlFor="ticket-assignee-trigger" className="block text-gray-500 mb-1" data-id="element-111" data-mp="1t2yvmwsbi6c1">
                      Assigned To
                    </label>
                    <button id="ticket-assignee-trigger" type="button" aria-expanded={isAssigneeMenuOpen} aria-controls="ticket-assignee-menu" aria-haspopup="listbox" onClick={() => {
                    setIsAssigneeMenuOpen(isOpen => !isOpen);
                    setAssigneeSearch('');
                  }} className="flex w-full items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white p-2 text-left text-sm font-medium text-gray-900 transition-colors hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-112" data-mp="1f4qv6ew2omw4">
                      <span className="min-w-0 truncate" data-id="element-113" data-mp="1i3evruoih2cm">{ticket.assignedTo}</span>
                      <ChevronDownIcon aria-hidden="true" className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${isAssigneeMenuOpen ? 'rotate-180' : ''}`} data-id="element-114" data-mp="1e1tp4r2klkav" />
                    </button>

                    {isAssigneeMenuOpen && <div id="ticket-assignee-menu" role="dialog" aria-label="Assign ticket" onKeyDown={event => {
                    if (event.key === 'Escape') {
                      event.preventDefault();
                      setIsAssigneeMenuOpen(false);
                    }
                  }} className="absolute z-40 mt-1 w-full min-w-[15rem] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg" data-id="element-115" data-mp="21t8tsqhtkdpa">
                        <div className="border-b border-gray-100 p-2" data-id="element-116" data-mp="2n8c1k8th3jd7">
                          <label className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2.5 py-2 focus-within:ring-2 focus-within:ring-blue-500" data-id="element-117" data-mp="2s884e4qfp4r6">
                            <SearchIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-400" data-id="element-118" data-mp="31zm0e2j6fmfk" />
                            <span className="sr-only" data-id="element-119" data-mp="361pgj4gsjf76">Search assignees</span>
                            <input ref={assigneeSearchRef} type="search" value={assigneeSearch} onChange={event => setAssigneeSearch(event.target.value)} placeholder="Search users" className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-400" data-id="element-120" data-mp="3azr86dsa8mu9" />
                          </label>
                        </div>
                        <div role="listbox" aria-label="Available assignees" className="max-h-52 overflow-y-auto p-1.5" data-id="element-121" data-mp="357laj1ahyu2f">
                          {matchingAssignees.length ? matchingAssignees.map(assignee => {
                        const isAssigned = ticket.assignedTo === assignee;
                        return <button key={assignee} type="button" role="option" aria-selected={isAssigned} onClick={() => handleAssigneeChange(assignee)} className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${isAssigned ? 'bg-blue-50 text-blue-800' : 'text-gray-700 hover:bg-slate-50'}`} data-id="element-122" data-mp="1ps48dedcgqna">
                                  <span aria-hidden="true" className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${isAssigned ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 bg-white'}`} data-id="element-123" data-mp="bvktlrp7zqnj">
                                    {isAssigned && <CheckIcon className="h-3 w-3" data-id="element-124" data-mp="2p2ke9kfono1i" />}
                                  </span>
                                  <span className="min-w-0 truncate" data-id="element-125" data-mp="2tzswfl0tzs2r">{assignee}</span>
                                </button>;
                      }) : <p className="px-2.5 py-5 text-center text-sm text-gray-500" data-id="element-126" data-mp="2k494tatavfjt">
                              No matching users.
                            </p>}
                        </div>
                      </div>}
                  </div>
                  <div data-id="element-127" data-mp="1wdlukivwwdu3">
                    <span className="block text-gray-500 mb-1" data-id="element-128" data-mp="1wfg5d4dgypkg">Created</span>
                    <div className="text-gray-900" data-id="element-129" data-mp="21bnaiuy1jx1c">
                      {new Date(ticket.createdDate).toLocaleString()}
                    </div>
                  </div>
                  <div data-id="element-130" data-mp="1csdb2m6mmyxz">
                    <span className="block text-gray-500 mb-1" data-id="element-131" data-mp="1csv4lb1wmz27">Category</span>
                    <div className="text-gray-900" data-id="element-132" data-mp="1rpm37i3r86qr">{ticket.category}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Activity & Comments */}
            <div className="md:col-span-2 flex flex-col h-full md:border-l md:border-gray-100 md:pl-8" data-id="element-133" data-mp="20ny2s9mlmevc">
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3 flex items-center" data-id="element-134" data-mp="1vpmui3byccf8">
                <MessageSquareIcon className="w-4 h-4 mr-2" data-id="element-135" data-mp="2aealrxerlzzd" />
                Activity & Comments
              </h3>

              {/* Comments List */}
              <div className="flex-1 space-y-4 mb-6" data-id="element-136" data-mp="2jf8psfp7e7hy">
                {comments.map(comment => <div key={comment.id} className="bg-gray-50 rounded-lg p-4 border border-gray-100" data-id="element-137" data-mp="2feycsj2zugtl">
                    <div className="flex justify-between items-start mb-2" data-id="element-138" data-mp="cpnkd6hske3y">
                      <div className="flex items-center space-x-2" data-id="element-139" data-mp="hoqhm4rj5g1d">
                        <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold" data-id="element-140" data-mp="miciz0s8txhv">
                          {comment.author.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                        </div>
                        <span className="font-medium text-sm text-gray-900" data-id="element-141" data-mp="3p8q3hmunoob">
                          {comment.author}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500" data-id="element-142" data-mp="1f4xd2903snp4">
                        {comment.timestamp}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap mt-1" data-id="element-143" data-mp="33pcw3i2n46lw">
                      {comment.text}
                    </p>
                  </div>)}
              </div>

              {/* Comment Input */}
              <div className="mt-auto border border-gray-200 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition-shadow" data-id="element-144" data-mp="2uvzu7qmlluno">
                <textarea value={commentText} onChange={e => setCommentText(e.target.value)} placeholder="Type your comment here..." className="w-full p-4 text-sm border-none focus:ring-0 resize-none min-h-[100px]" data-id="element-145" data-mp="2l0693iutcsua" />
                <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-between" data-id="element-146" data-mp="270djxryxqkof">
                  <button onClick={handleTagSubmitter} className="flex items-center space-x-1 text-xs font-medium text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2.5 py-1.5 rounded-md transition-colors" data-id="element-147" data-mp="a6jxkf0p7bq4">
                    <AtSignIcon className="w-3.5 h-3.5" data-id="element-148" data-mp="t4c7njo2gv51" />
                    <span data-id="element-149" data-mp="fbw5sm6wj35p">Tag Submitter</span>
                  </button>
                  <button onClick={handlePostComment} disabled={!commentText.trim()} className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors" data-id="element-150" data-mp="f9t2x1knhfjk">
                    <span data-id="element-151" data-mp="y2dpy2do4qfg">Post</span>
                    <SendIcon className="w-4 h-4" data-id="element-152" data-mp="y1c73bqeuzn6" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}